// @ts-strict-ignore
import * as React from 'react';
import { BuildDetails } from '../../types';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import * as API from '../../api';
import { RouteComponentProps } from 'react-router-dom';
import qs from 'query-string';
import {
    selectAppPlatform,
    selectAppSettings,
    selectAuthCredentials,
    selectEffectiveTimezone,
} from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { globalMessage } from '../../utils/globalMessage';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import AuthedUrlButton from '../authedUrlButton/AuthedUrlButton';
import Text from 'antd/lib/typography/Text';
import { CloudDownloadOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import { helperTextForArtifactType } from '../../helpers/displayHelpers';
import { platformLabelForAppName } from '../../helpers/mdmPlatformOverride';
import Avatar from 'antd/lib/avatar';
import { antGrey } from '../styles/globalStyles';
import { Content } from 'antd/lib/layout/layout';
import { getShouldDisableInstallationForBinaryType } from '../../helpers';
import { MobileOutlined } from '@ant-design/icons';
import Spin from 'antd/lib/spin';
import { isMobile } from 'react-device-detect';
import { buildListDataSource } from '../buildDistribution/CommonComponents';
import { DrawerList } from '../buildDistribution/mobileComponents/ResponsiveDrawer';

interface BuildDetailsComponentProps extends ReleaseComponentProps, RouteComponentProps {}

const BuildDetailsComponent: React.FC<BuildDetailsComponentProps> = (props: BuildDetailsComponentProps) => {
    const { orgId, appId, releaseId } = props;

    const [buildDetails, setBuildDetails] = React.useState<BuildDetails>(null);
    const [buildDetailsIsLoading, setBuildDetailsIsLoading] = React.useState(false);

    const auth = useSelector((state: RootState) => selectAuthCredentials(state));

    const appName = useSelector((state: RootState) => selectAppSettings(state, props.appId).appName);
    const appImageUrl = useSelector((state: RootState) => selectAppSettings(state, props.appId).appImageUrl);
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const platformLabel = useSelector((state: RootState) =>
        platformLabelForAppName(selectAppSettings(state, props.appId)),
    );
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const qParams = qs.parse(props.location.search);
    const buildDetailsSecret = qParams?.secret as string;

    React.useEffect(() => {
        if (buildDetailsSecret) {
            setBuildDetailsIsLoading(true);
            API.getBuildDetails({ orgId, appId, releaseId, buildDetailsSecret }, auth)
                .then((response) => {
                    setBuildDetails(response);
                })
                .catch((error) => {
                    globalMessage.error(error.message);
                })
                .finally(() => {
                    setBuildDetailsIsLoading(false);
                });
        }
    }, []);

    return (
        <Content
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                padding: 16,
                paddingBottom: 86,
                overflow: 'auto',
            }}
        >
            <Space direction="vertical" size="large" align="center">
                <Avatar
                    shape="square"
                    size={64}
                    src={appImageUrl}
                    style={{
                        borderRadius: 12,
                        boxShadow: 'var(--rw-shadow-sm)',
                    }}
                >
                    {appName.charAt(0).toUpperCase()}
                </Avatar>
                <Title level={4}>
                    {appName}
                    {platformLabel && (
                        <>
                            {' '}
                            <span style={{ color: antGrey.grey7, fontWeight: 300 }}>({platformLabel})</span>
                        </>
                    )}
                </Title>
            </Space>
            {buildDetailsIsLoading && <Spin spinning />}
            {buildDetails && (
                <>
                    <Space direction="vertical" size="large" align="center" style={{ marginTop: 16 }}>
                        <div
                            style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                                paddingLeft: 12,
                                paddingRight: 12,
                                borderRadius: 8,
                                backgroundColor: antGrey.grey3,
                            }}
                        >
                            <Space align="center">
                                <MobileOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ fontSize: 16 }}>Build #{buildDetails.binaryBuild.buildNumber}</Text>
                            </Space>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                            <AuthedUrlButton
                                // TODO: add proper user action?
                                actionType={RunwayUserAction.UADownloadBuildArtifact}
                                onLinkAuthed={(authedDownloadLink) => window.open(authedDownloadLink, '_self')}
                                linkUrl={buildDetails?.installationUrl}
                                loading={buildDetailsIsLoading}
                                type="primary"
                                icon={<CloudDownloadOutlined />}
                                size="large"
                                disabled={
                                    getShouldDisableInstallationForBinaryType(buildDetails.binaryBuild.buildType) ||
                                    !isMobile
                                }
                                style={{ width: 300 }}
                            >
                                Install
                            </AuthedUrlButton>
                            <Text type="secondary" style={{ textAlign: 'center' }}>
                                {helperTextForArtifactType(
                                    buildDetails.binaryBuild?.buildType,
                                    buildDetails.binaryBuild.provisioningProfile?.name,
                                )}
                            </Text>
                            {!isMobile && (
                                <Text type="warning" style={{ textAlign: 'center' }}>
                                    This build can only be installed from a mobile device
                                </Text>
                            )}
                        </div>
                    </Space>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: isMobile ? 'stretch' : 'center',
                            marginTop: 20,
                        }}
                    >
                        {(() => {
                            const [firstGroup, secondGroup] = buildListDataSource(
                                props.appId,
                                props.orgId,
                                buildDetails,
                                appPlatform,
                                effectiveTimezone,
                            );

                            return (
                                <>
                                    <DrawerList items={firstGroup} />
                                    <DrawerList items={secondGroup} />
                                </>
                            );
                        })()}
                    </div>
                </>
            )}
        </Content>
    );
};

export default BuildDetailsComponent;
